import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const NewtownardsChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Treatment Newtownards | Fibromyalgia Hypnotherapy County Down</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Newtownards from Belfast clinic (12 miles). Neuroscience approach for fibromyalgia, back pain, arthritis. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Newtownards, fibromyalgia hypnotherapy Newtownards, back pain treatment Newtownards, arthritis pain Newtownards, pain management County Down" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newtownards-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Treatment Newtownards | Fibromyalgia Hypnotherapy County Down" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Newtownards. Neuroscience approach for fibromyalgia, back pain, arthritis." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Treatment Newtownards",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Newtownards and County Down from Belfast clinic. Specializing in fibromyalgia, chronic back pain, arthritis, migraines, and tension headaches with NHS-complementary approach. Ultra-convenient 12 miles for commuter professionals.",
            "url": "https://www.sharonclare-hypnotherapy.com/newtownards-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Newtownards",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Newtownards from Belfast clinic?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is just 12 miles from Newtownards via the A20, taking only 20-25 minutes by car in normal traffic. This ultra-convenient distance makes regular pain management appointments highly practical. Many Newtownards commuters already travel to Belfast for work, making specialist pain treatment easy to integrate into your routine. You can schedule appointments before or after work, or during lunch breaks if needed."
                }
              },
              {
                "@type": "Question",
                "name": "Can I fit pain treatment around work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. Clinic days and times vary, with early evening appointments available. At just 12 miles from Newtownards, you can realistically schedule appointments before work, during extended lunch breaks, or after work without consuming your entire day. Many commuter professionals find this distance highly manageable for regular weekly or fortnightly sessions. Online sessions are also available if you prefer maximum flexibility or want to save commute time on particular weeks."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for desk work pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for desk work pain including chronic back pain, neck pain, shoulder tension, and tension headaches from prolonged sitting and computer work. Many Newtownards commuter professionals experience pain from poor ergonomics, stress, and sedentary work patterns. When you combine physical strain from sitting 8+ hours with work stress and commuter fatigue, your nervous system amplifies pain signals. Hypnotherapy calms this overactive alarm system, reduces muscle tension, and helps your brain process pain differently. Many desk workers report significant improvements in pain levels and productivity."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with tension headaches?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy can be very effective for tension headaches and migraines. Tension headaches often result from muscle tightness in the neck and shoulders combined with stress - both common in Newtownards commuter professionals juggling work demands, travel, and family responsibilities. Hypnotherapy works by reducing overall stress levels, teaching deep relaxation, releasing muscle tension, and helping your brain develop healthier pain processing patterns. Many clients experience reduced frequency and intensity of headaches, with some reporting complete resolution after several sessions."
                }
              },
              {
                "@type": "Question",
                "name": "How does commuter stress worsen chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Commuter stress keeps your nervous system on high alert, which directly amplifies pain perception. When you're stressed from traffic, work pressures, time constraints, or juggling multiple responsibilities, your brain interprets this as threat and keeps pain signals turned up as a protective mechanism. Additionally, commuting posture (sitting in cars or on buses) creates physical strain on back, neck, and shoulders. For Newtownards professionals commuting to Belfast, this combination of stress and physical strain can significantly worsen chronic pain. Hypnotherapy addresses both the stress amplification and the muscle tension patterns."
                }
              },
              {
                "@type": "Question",
                "name": "Is evening availability offered?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes - clinic days and times vary, and early evening appointments are available, which accommodates Newtownards commuter professionals who work standard office hours. The short 12-mile run along the A20 means even an early evening appointment doesn't require extensive travel time. Many clients schedule regular weekly sessions after work, making pain management fit seamlessly into their routine without needing to take time off work."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Newtownards
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy ultra-convenient from Newtownards
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Just 12 miles</span> from Newtownards via A20 • <span className="font-semibold">20-25 min drive</span>
                </p>
              </div>
              <p className="text-gray-600">Ultra-convenient for regular in-person sessions</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Newtownards
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Newtownards and County Down from our Belfast clinic, located just 12 miles west via the A20. The journey takes only 20-25 minutes, making this one of the most accessible specialist pain management services for Newtownards residents. For commuter professionals dealing with persistent pain conditions like desk work back pain, tension headaches, fibromyalgia, or arthritis, this ultra-convenient distance means regular in-person hypnotherapy sessions are genuinely practical without consuming significant time or creating additional stress.
              </p>
              <p>
                Living with chronic pain in Newtownards comes with specific pressures that affect how pain feels. As a commuter town serving Belfast's professional workforce, many Newtownards residents experience the particular strain of balancing demanding office work with family responsibilities and daily commuting. Desk work creates chronic back pain, neck pain, shoulder tension, and tension headaches from prolonged sitting and computer use. Add the stress of commuting, tight work deadlines, parenting demands for dual-income families, and the time poverty of juggling multiple responsibilities, and your nervous system stays on high alert, significantly amplifying pain signals.
              </p>
              <p>
                Modern pain neuroscience shows us that chronic pain is processed in the nervous system, not just at the injury site. When we're stressed from work demands, commuter fatigue, time pressures, or the accumulated tension of professional life, our brain's alarm system becomes hypersensitive and turns up the volume on pain signals. This is why the same desk work back pain can feel worse during stressful projects, or why tension headaches worsen when you're juggling work and family demands. This is where solution-focused hypnotherapy excels. By calming your overactive nervous system, teaching your brain healthier pain processing patterns, and addressing the stress that intensifies pain, many Newtownards clients experience significant reductions in pain levels and improvements in work productivity and quality of life.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I bring medical understanding to chronic pain treatment. I understand conditions like chronic back pain from desk work, tension headaches, fibromyalgia, and arthritis from both a clinical and therapeutic perspective. I know how frustrating it is when you're told to "improve your ergonomics" or "reduce stress" without practical tools to actually do so, or when medications help a bit but don't address why stress makes the pain worse. Hypnotherapy offers a science-backed approach that works alongside your medical care to help you manage pain better, function more effectively at work, and improve your quality of life. This isn't about ignoring the pain - it's about retraining your nervous system to respond differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. This is why chronic pain can feel disproportionate to actual tissue damage, and why work stress or commuter fatigue can worsen pain intensity. Your nervous system's volume control is turned up too high.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress from work deadlines, commuting pressures, time poverty from juggling professional and family responsibilities keeps your survival system on high alert. When your brain perceives threat - even from work emails or tight schedules - it keeps pain signals amplified as a protective mechanism. Calming this system is essential for pain reduction.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Neuroplasticity</h3>
                <p className="text-gray-700">
                  The brain learns pain pathways through repetition. The more they're used, the easier they fire - like a well-worn commuter route. But neuroplasticity also means we can create new, healthier pathways. Your brain can learn to process pain differently, and hypnotherapy facilitates that relearning process.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well. For Newtownards commuter professionals dealing with the physical demands of desk work combined with professional stress and time poverty, this overprotective system often gets stuck in overdrive. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly important for Newtownards professionals dealing with the physical demands of desk work, commuter stress, tight deadlines, and the accumulated tension of balancing work and family life in dual-income households.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of pain messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways. Neuroscience research shows the brain can literally turn down the volume on pain signals when it feels safe and calm.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Harness Neuroplasticity</h3>
                    <p className="text-gray-700">
                      Creates new, healthier neural pathways so your brain can learn to interpret sensations differently. The brain's remarkable ability to change means chronic pain doesn't have to be permanent. We can retrain your nervous system to respond to pain signals in a healthier way.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use at your desk, during your commute, or at home to manage pain flare-ups, reduce stress, and improve sleep - all factors that significantly influence pain levels. These are practical tools for busy professional life, not just the therapy room.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. Studies show hypnotherapy can significantly reduce pain intensity for conditions like chronic back pain from desk work, tension headaches, fibromyalgia, and arthritis by working with the brain's natural pain control mechanisms. A major review found hypnotherapy 68% more effective than no treatment for chronic pain conditions.
              </p>
              <p className="text-gray-700 text-lg">
                For Newtownards residents, the ultra-convenient 12-mile distance to Belfast means specialist pain management is genuinely accessible without significant travel burden. At just 20-25 minutes, you can realistically schedule weekly or fortnightly sessions without them consuming your limited free time. Many commuter professionals find they can fit appointments around their Belfast work schedule, making pain management practical and sustainable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Belfast Trust waiting times often exceeding 12 months for pain clinic appointments, many Newtownards residents need support now while waiting for specialist care. Hypnotherapy can provide that bridge, offering relief and improved functioning while you wait for or work alongside NHS treatment.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy as part of a multidisciplinary approach alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you regain confidence, mobility, and ability to work effectively despite pain.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter. This medical background means I understand when something needs medical attention versus when hypnotherapy can help, and I'll always support you in getting appropriate medical care.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work with your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Newtownards
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the A20 west into Belfast, then south along the Ormeau Road (A24) to number 228
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Just 12 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 20-25 minutes in normal traffic
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Public transport:</span> Metro services run along the Ormeau Road corridor, with Botanic and City Hospital the nearest rail stations
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Ultra-Convenient Access</h3>
                <p className="text-gray-700">
                  At just 12 miles and 20-25 minutes from Newtownards, Belfast is ultra-convenient for regular pain management appointments. Many Newtownards professionals already commute to Belfast for work, making specialist pain treatment easy to integrate into your routine. You can schedule sessions before or after work, during extended lunch breaks, or on days off without significant time investment. The minimal travel burden makes consistent, regular treatment genuinely sustainable for busy professionals managing chronic pain.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Also Available</h3>
              <p className="text-gray-700">
                While the 12-mile distance makes in-person sessions highly practical, I also offer secure online hypnotherapy sessions via video call for maximum flexibility. Online sessions provide the same professional treatment from your home in Newtownards. This option is ideal for those experiencing high pain days, anyone juggling particularly demanding work schedules, or those who prefer the convenience of online therapy. You can mix in-person and online sessions based on your schedule and pain levels.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What County Down Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My chronic back pain from years of desk work and commuting was affecting my job performance and my ability to play with my kids. The drive from Newtownards to Belfast was so short - just 20 minutes - that I could easily fit sessions around work. Sharon's hypnotherapy helped me understand how stress was amplifying the pain. After 6 sessions, my pain levels dropped significantly and I can sit through meetings without agony. Her nursing background meant she really understood the ergonomic and stress factors."
                </p>
                <p className="font-semibold text-gray-900">A.K., Newtownards - Chronic Back Pain from Desk Work</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My tension headaches were getting worse every month - work stress, commuting, parenting, I was constantly tense. The hypnotherapy with Sharon made a massive difference. She taught me techniques I could use at my desk when I felt tension building. My headaches are much less frequent now and I'm managing work stress better. The convenience of the Belfast clinic meant I could schedule appointments after work easily."
                </p>
                <p className="font-semibold text-gray-900">S.M., County Down - Tension Headaches</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia as a working professional was exhausting. The pain, the fatigue, trying to maintain productivity - it was overwhelming. Sharon's online sessions were perfect for my schedule and her approach helped me manage my pain in ways the NHS couldn't offer. I'm sleeping better, my pain flare-ups are less severe, and I feel like I have some control back. I can actually get through work weeks now without complete exhaustion."
                </p>
                <p className="font-semibold text-gray-900">R.H., North Down - Fibromyalgia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Newtownards Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Newtownards from Belfast clinic?
                </h3>
                <p className="text-gray-700">
                  Belfast is just 12 miles from Newtownards via the A20, taking only 20-25 minutes by car in normal traffic. This ultra-convenient distance makes regular pain management appointments highly practical. Many Newtownards commuters already travel to Belfast for work, making specialist pain treatment easy to integrate into your routine. You can schedule appointments before or after work, or during lunch breaks if needed.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I fit pain treatment around work?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Clinic days and times vary, with early evening appointments available. At just 12 miles from Newtownards, you can realistically schedule appointments before work, during extended lunch breaks, or after work without consuming your entire day. Many commuter professionals find this distance highly manageable for regular weekly or fortnightly sessions. Online sessions are also available if you prefer maximum flexibility or want to save commute time on particular weeks.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for desk work pain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for desk work pain including chronic back pain, neck pain, shoulder tension, and tension headaches from prolonged sitting and computer work. Many Newtownards commuter professionals experience pain from poor ergonomics, stress, and sedentary work patterns. When you combine physical strain from sitting 8+ hours with work stress and commuter fatigue, your nervous system amplifies pain signals. Hypnotherapy calms this overactive alarm system, reduces muscle tension, and helps your brain process pain differently. Many desk workers report significant improvements in pain levels and productivity.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with tension headaches?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy can be very effective for tension headaches and migraines. Tension headaches often result from muscle tightness in the neck and shoulders combined with stress - both common in Newtownards commuter professionals juggling work demands, travel, and family responsibilities. Hypnotherapy works by reducing overall stress levels, teaching deep relaxation, releasing muscle tension, and helping your brain develop healthier pain processing patterns. Many clients experience reduced frequency and intensity of headaches, with some reporting complete resolution after several sessions.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does commuter stress worsen chronic pain?
                </h3>
                <p className="text-gray-700">
                  Commuter stress keeps your nervous system on high alert, which directly amplifies pain perception. When you're stressed from traffic, work pressures, time constraints, or juggling multiple responsibilities, your brain interprets this as threat and keeps pain signals turned up as a protective mechanism. Additionally, commuting posture (sitting in cars or on buses) creates physical strain on back, neck, and shoulders. For Newtownards professionals commuting to Belfast, this combination of stress and physical strain can significantly worsen chronic pain. Hypnotherapy addresses both the stress amplification and the muscle tension patterns.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is evening availability offered?
                </h3>
                <p className="text-gray-700">
                  Yes - clinic days and times vary, and early evening appointments are available, which accommodates Newtownards commuter professionals who work standard office hours. The short 12-mile run along the A20 means even an early evening appointment doesn't require extensive travel time. Many clients schedule regular weekly sessions after work, making pain management fit seamlessly into their routine without needing to take time off work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with chronic pain, book your initial consultation today and start retraining your brain to turn down the volume on pain."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default NewtownardsChronicPain;
